import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Typography, Tooltip, IconButton, TablePagination, Dialog, DialogTitle,
    DialogContent, DialogActions, Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDate } from '../../../utils/helpers';
import adminService from '../../../services/adminService';
import AdminToolbar from '../components/AdminToolbar';
import { toast } from 'react-toastify';

const TransactionManagement = () => {
    const [transactions, setTransactions] = useState([]);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('all');

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    // Delete confirmation state
    const [deleteId, setDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setPage(0); // Reset to page 0 when filtering
    }, [search, filterType]);

    useEffect(() => {
        fetchTransactions();
    }, [search, filterType, page, rowsPerPage]);

    const fetchTransactions = async () => {
        const params = {
            page: page + 1,
            limit: rowsPerPage
        };
        if (search) params.search = search;
        if (filterType !== 'all') params.type = filterType;

        const res = await adminService.getTransactions(params);
        if (res.success) {
            setTransactions(res.data);
            setTotalRows(res.total || 0);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
    };

    const handleConfirmDelete = async () => {
        if (!deleteId) return;
        setIsDeleting(true);
        try {
            const res = await adminService.deleteTransaction(deleteId);
            if (res.success) {
                toast.success(res.message || 'Đã ẩn giao dịch thành công');
                fetchTransactions();
            } else {
                toast.error(res.message || 'Lỗi khi ẩn giao dịch');
            }
        } catch (err) {
            toast.error('Lỗi kết nối máy chủ');
        } finally {
            setIsDeleting(false);
            setDeleteId(null);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Đã sao chép!');
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f1f5f9', mb: 3 }}>Lịch Sử Giao Dịch & Nạp Tiền</Typography>

            <AdminToolbar
                searchValue={search}
                onSearchChange={setSearch}
                filterValue={filterType}
                onFilterChange={setFilterType}
                filterOptions={[
                    { value: 'all', label: 'Tất cả loại GD' },
                    { value: 'DEPOSIT', label: 'Nạp tiền' },
                    { value: 'PAYMENT', label: 'Thanh toán' },
                    { value: 'REFUND', label: 'Hoàn tiền' }
                ]}
                placeholder="Tìm user, mã GD, nội dung..."
            />

            <TableContainer component={Paper} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#0f172a' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Mã GD</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Khách Hàng</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Loại & Nội Dung</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Số Tiền</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Số Dư Sau</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Mã Ngân Hàng</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Thời Gian</TableCell>
                            <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Trạng Thái</TableCell>
                            <TableCell align="right" sx={{ color: '#94a3b8', fontWeight: 'bold' }}>Thao Tác</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions.map((trans) => (
                            <TableRow key={trans.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                                <TableCell sx={{ color: 'white' }}>
                                    <Tooltip title={trans.id} arrow>
                                        <Typography fontFamily="monospace" variant="caption" sx={{ color: '#64748b' }}>
                                            {trans.id.substring(0, 8)}...
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    <Typography fontWeight="bold" variant="body2">{trans.wallet?.user?.username || 'Unknown'}</Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>{trans.wallet?.user?.email}</Typography>
                                </TableCell>
                                <TableCell sx={{ color: 'white', maxWidth: 200 }}>
                                    <Box display="flex" flexDirection="column">
                                        <Typography fontWeight="bold" color={trans.type === 'DEPOSIT' ? '#4ade80' : '#f87171'} variant="body2">
                                            {trans.type === 'DEPOSIT' ? 'Nạp Tiền' : (trans.type === 'PAYMENT' ? 'Thanh Toán' : trans.type)}
                                        </Typography>
                                        <Tooltip title={trans.content || trans.description}>
                                            <Typography variant="caption" color="#94a3b8" noWrap>
                                                {trans.description}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: trans.amount > 0 ? '#4ade80' : '#f87171' }}>
                                    {trans.amount > 0 ? '+' : ''}{Number(trans.amount).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ color: '#94a3b8' }}>
                                    {Number(trans.balanceAfter).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ color: 'white' }}>
                                    {trans.transactionCode ? (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="#f59e0b">
                                                {trans.transactionCode}
                                            </Typography>
                                            <IconButton size="small" onClick={() => handleCopy(trans.transactionCode)} sx={{ color: '#64748b', p: 0.5 }}>
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    ) : '-'}
                                </TableCell>
                                <TableCell sx={{ color: '#94a3b8' }}>
                                    {formatDate(trans.createdAt, 'dd/MM/yyyy HH:mm')}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={trans.status}
                                        color={trans.status === 'COMPLETED' ? 'success' : (trans.status === 'PENDING' ? 'warning' : 'error')}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Ẩn lịch sử (Soft Delete)">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleDeleteClick(trans.id)}
                                            sx={{ 
                                                color: '#ef4444', 
                                                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', transform: 'scale(1.1)' },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {transactions.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                                    Không tìm thấy giao dịch nào
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ color: 'white' }}
            />

            {/* Confirmation Dialog */}
            <Dialog 
                open={Boolean(deleteId)} 
                onClose={() => setDeleteId(null)}
                PaperProps={{
                    sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    Xác nhận ẩn giao dịch
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography>
                        Bạn có chắc chắn muốn ẩn lịch sử giao dịch này không? 
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                        Lưu ý: Giao dịch sẽ không còn hiển thị ở danh sách Admin/User nhưng vẫn được lưu trữ trong hệ thống để đối soát.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <Button onClick={() => setDeleteId(null)} sx={{ color: '#94a3b8' }}>Hủy</Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        variant="contained" 
                        color="error"
                        disabled={isDeleting}
                        sx={{ borderRadius: 2, fontWeight: 'bold' }}
                    >
                        {isDeleting ? 'Đang thực hiện...' : 'Đồng ý ẩn'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransactionManagement;
